import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Zap, Sparkles, Loader2, CheckCircle2, AlertTriangle, ArrowUpRight, CalendarClock, Download, History, RotateCcw } from "lucide-react";

const fmtMoney = (cents) => (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return "—"; }
};

const PLAN_META = {
  free: { name: "Free", Icon: Sparkles, accent: "text-slate-300", blurb: "1 AI analysis per day" },
  pro: { name: "Netroy Pro", Icon: Zap, accent: "text-brand", blurb: "5 AI analyses per day" },
  elite: { name: "Netroy Elite", Icon: Crown, accent: "text-amber", blurb: "Unlimited AI analyses" },
};

export const ManageSubscription = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/payments/subscription"),
        api.get("/payments/history"),
      ]);
      setSub(s.data);
      setHistory(h.data.items || []);
    } catch (e) { console.error("Failed to load subscription/history:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resume = async () => {
    setResuming(true);
    try {
      await api.post("/payments/resume");
      toast.success("Cancellation reversed — your plan will keep renewing");
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not undo");
    } finally { setResuming(false); }
  };

  const downloadInvoice = (sessionId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/payments/invoice/${sessionId}`;
    window.open(url, "_blank");
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/payments/cancel");
      toast.success("Subscription set to cancel at period end");
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel");
    } finally { setCancelling(false); }
  };

  if (loading) {
    return <div className="py-16 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>;
  }

  const plan = sub?.plan || "free";
  const meta = PLAN_META[plan];
  const isPaid = plan === "pro" || plan === "elite";
  const cancelScheduled = sub?.cancel_at_period_end;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
      <div data-testid="manage-subscription-card" className="card-surface rounded-2xl p-7">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg border border-white/10 bg-white/5 grid place-items-center">
              <meta.Icon className={`h-6 w-6 ${meta.accent}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 data-testid="subscription-plan-name" className="font-heading text-xl font-extrabold tracking-tight">{meta.name}</h3>
                {isPaid && !cancelScheduled && (
                  <span className="inline-flex items-center gap-1 font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded bg-gain/12 text-gain border border-gain/30">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                )}
                {cancelScheduled && (
                  <span className="inline-flex items-center gap-1 font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber/12 text-amber border border-amber/30">
                    <AlertTriangle className="h-3 w-3" /> Cancels soon
                  </span>
                )}
              </div>
              <p className="font-data text-[11px] text-slate-500 mt-0.5">{meta.blurb}</p>
            </div>
          </div>
          {isPaid && (
            <div className="text-right">
              <div data-testid="subscription-amount" className="font-heading text-2xl font-black">${fmtMoney(sub.amount || 0)}</div>
              <div className="font-data text-[10px] uppercase tracking-widest text-slate-500">/ {sub.plan_interval === "year" ? "year" : "month"}</div>
            </div>
          )}
        </div>

        {isPaid ? (
          <div className="mt-6 rounded-lg bg-[#0C1017] border border-white/8 p-4 flex items-center gap-3">
            <CalendarClock className="h-5 w-5 text-brand shrink-0" />
            <div className="flex-1">
              <div className="font-data text-[10px] uppercase tracking-widest text-slate-500">
                {cancelScheduled ? "Access ends on" : "Renews on"}
              </div>
              <div data-testid="subscription-renewal-date" className="text-sm text-white font-medium mt-0.5">{fmtDate(sub.plan_expires)}</div>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-lg bg-brand/5 border border-brand/20 p-4">
            <p className="text-sm text-slate-300">You're on the Free plan — 1 AI chart analysis per day. Upgrade for more firepower and unlimited insights.</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            data-testid="subscription-switch-plans-btn"
            onClick={() => navigate("/#pricing")}
            className={`${isPaid ? "bg-white/8 hover:bg-white/15 border border-white/10" : "bg-brand hover:bg-brand-hover"} text-white font-semibold gap-2`}
          >
            {isPaid ? "Switch / Upgrade Plan" : "Choose a Plan"} <ArrowUpRight className="h-4 w-4" />
          </Button>

          {isPaid && !cancelScheduled && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="subscription-cancel-btn" variant="outline" disabled={cancelling}
                  className="bg-transparent border-loss/30 text-loss hover:bg-loss/10 hover:text-loss gap-2">
                  {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Cancel Subscription
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0C1017] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading">Cancel your {meta.name} plan?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    You'll keep full access until {fmtDate(sub.plan_expires)}. After that your account reverts to the Free plan (1 analysis/day). You won't be charged again.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Keep Plan</AlertDialogCancel>
                  <AlertDialogAction data-testid="subscription-cancel-confirm-btn" onClick={cancel} className="bg-loss hover:bg-loss/80 text-white">
                    Yes, cancel
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {cancelScheduled && (
            <Button data-testid="subscription-undo-cancel-btn" onClick={resume} disabled={resuming}
              className="bg-brand hover:bg-brand-hover text-white font-semibold gap-2">
              {resuming ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Undo Cancellation
            </Button>
          )}
        </div>

        <p className="font-data text-[10px] text-slate-600 mt-5">Billing secured by Stripe · Test mode · Cancel anytime</p>
      </div>

      {/* Billing history */}
      <div data-testid="billing-history-card" className="card-surface rounded-2xl p-7 mt-4">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-brand" />
          <h3 className="font-heading text-lg font-bold">Billing History</h3>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">No payments yet. Your invoices will appear here after your first upgrade.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {history.map((h) => (
              <div key={h.session_id} data-testid="billing-history-row" className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm text-white font-medium">Netroy {h.plan.charAt(0).toUpperCase() + h.plan.slice(1)} · {h.interval === "year" ? "Annual" : "Monthly"}</div>
                  <div className="font-data text-[11px] text-slate-500">{(h.date || "").slice(0, 10)} · {h.status}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-data text-sm text-white">{h.currency} {(h.amount / 100).toFixed(2)}</span>
                  <Button data-testid="invoice-download-btn" onClick={() => downloadInvoice(h.session_id)} variant="outline" size="sm"
                    className="bg-transparent border-white/15 text-slate-300 hover:bg-white/5 hover:text-white gap-1.5 h-8">
                    <Download className="h-3.5 w-3.5" /> Invoice
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};
